/**
 * PartnerPriorityDrawer — admin / dispatcher priority-list manager for
 * a single Partner, opened from the Partners page.
 *
 * Three concerns, one drawer:
 *   1. Browse the partner's current priority dispensaries (full list,
 *      with assigned-rep + per-pairing notes + pause toggle + remove)
 *   2. Add a single priority via dispensary typeahead search
 *   3. Bulk-import a pasted list (license #s or names) → review-and-
 *      confirm screen that resolves ambiguous rows before committing.
 *
 * Write actions are admin / dispatcher only — the backend enforces,
 * and the UI hides the write affordances for sales reps who read the
 * drawer in read-only mode.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Search,
  Trash2,
  Pause,
  Play,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
  Plus,
  FileText,
  Save,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  fetchPartnerPriorities,
  addPartnerPriority,
  updatePartnerPriority,
  removePartnerPriority,
  previewBulkImportPriorities,
  confirmBulkImportPriorities,
  partnerInitials,
} from "../lib/api";
import { api } from "../lib/api";

export default function PartnerPriorityDrawer({ partner, onClose, canWrite }) {
  const [rows, setRows] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const searchTimer = useRef(null);

  const load = async () => {
    try {
      const data = await fetchPartnerPriorities(partner.id);
      setRows(data);
    } catch (err) {
      toast.error("Could not load priorities", {
        description: err?.response?.data?.detail || err?.message,
      });
      setRows([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [partner.id]);

  // --- Dispensary typeahead search (debounced) ---
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/dispensaries", {
          params: { q: query, limit: 10 },
        });
        setResults(data || []);
      } catch {
        setResults([]);
      }
    }, 220);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [query]);

  const existingIds = useMemo(
    () => new Set((rows || []).map((r) => r.dispensary_id)),
    [rows]
  );

  const onAdd = async (dispensary_id) => {
    if (existingIds.has(dispensary_id)) {
      toast.info("Already on this partner's list");
      return;
    }
    setAdding(true);
    try {
      await addPartnerPriority(partner.id, { dispensary_id });
      toast.success("Added to priority list");
      setQuery("");
      setResults([]);
      load();
    } catch (err) {
      toast.error("Could not add", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setAdding(false);
    }
  };

  const onToggleStatus = async (row) => {
    const next = row.status === "active" ? "paused" : "active";
    try {
      await updatePartnerPriority(partner.id, row.dispensary_id, {
        status: next,
      });
      toast.success(next === "active" ? "Resumed" : "Paused");
      load();
    } catch (err) {
      toast.error("Could not update", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const onEditNotes = async (row, notes) => {
    if (notes === row.partner_notes) return;
    try {
      await updatePartnerPriority(partner.id, row.dispensary_id, {
        partner_notes: notes,
      });
      toast.success("Notes saved");
      load();
    } catch (err) {
      toast.error("Could not save notes", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const onRemove = async (row) => {
    if (
      !window.confirm(
        `Remove "${row.dispensary_nickname || row.dispensary_name}" from ${
          partner.name
        }'s priority list?`
      )
    )
      return;
    try {
      await removePartnerPriority(partner.id, row.dispensary_id);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error("Could not remove", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const activeCount = (rows || []).filter((r) => r.status === "active").length;
  const pausedCount = (rows || []).filter((r) => r.status === "paused").length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="partner-priority-drawer"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0"
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-zinc-50 flex items-center gap-2 flex-wrap">
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-mono font-bold"
                  style={{
                    background: partner.brand_color || "#64748b",
                    color: "#0f172a",
                  }}
                >
                  {partnerInitials(partner.name)}
                </span>
                <span className="truncate">{partner.name}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                  Priority list
                </span>
              </DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">
                {activeCount} active · {pausedCount} paused · retailers this
                partner wants prioritized.
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Add + Bulk toolbar */}
        {canWrite && (
          <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="priority-search-add"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search dispensary by name, nickname, or license #…"
                className="pl-8 bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
                disabled={adding}
              />
              {results.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-zinc-950 border border-zinc-800 rounded shadow-lg max-h-72 overflow-y-auto z-30">
                  {results.map((d) => (
                    <button
                      key={d.id}
                      data-testid={`priority-search-result-${d.id}`}
                      onClick={() => onAdd(d.id)}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-900 border-b border-zinc-900 last:border-b-0"
                      disabled={existingIds.has(d.id)}
                    >
                      <div className="text-sm text-zinc-100 flex items-center gap-2 flex-wrap">
                        {d.nickname || d.name}
                        {d.nickname && (
                          <span className="text-[10px] font-mono text-zinc-500">
                            ({d.name})
                          </span>
                        )}
                        {existingIds.has(d.id) && (
                          <span className="text-[10px] font-mono text-amber-400">
                            already on list
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                        {d.city} · {d.dcc_license_number || "no license"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              data-testid="priority-bulk-import-btn"
              onClick={() => setBulkOpen(true)}
              variant="outline"
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-9"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              Bulk import
            </Button>
          </div>
        )}

        {/* List */}
        <div className="overflow-y-auto flex-1 px-6 py-3">
          {rows === null ? (
            <div className="text-xs font-mono text-zinc-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div
              data-testid="priority-empty"
              className="panel p-8 text-center text-sm text-zinc-500"
            >
              No priority dispensaries yet.
              {canWrite &&
                " Search above or Bulk import to paste a list from the partner."}
            </div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {rows.map((r) => (
                <PriorityRow
                  key={r.id}
                  row={r}
                  canWrite={canWrite}
                  onToggleStatus={() => onToggleStatus(r)}
                  onEditNotes={(n) => onEditNotes(r, n)}
                  onRemove={() => onRemove(r)}
                />
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-800">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="priority-drawer-close"
            className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
          >
            Done
          </Button>
        </DialogFooter>

        {bulkOpen && (
          <BulkImportDialog
            partner={partner}
            existingIds={existingIds}
            onClose={() => setBulkOpen(false)}
            onDone={() => {
              setBulkOpen(false);
              load();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Priority row ----------------

function PriorityRow({ row, canWrite, onToggleStatus, onEditNotes, onRemove }) {
  const [notes, setNotes] = useState(row.partner_notes || "");
  const [dirty, setDirty] = useState(false);
  const paused = row.status === "paused";

  // Reset editor when the backend row refreshes
  useEffect(() => {
    setNotes(row.partner_notes || "");
    setDirty(false);
  }, [row.partner_notes]);

  const display =
    row.dispensary_nickname || row.dispensary_name || "Unknown dispensary";

  return (
    <li
      data-testid={`priority-row-${row.dispensary_id}`}
      className={`panel p-3 flex flex-col gap-2 ${
        paused ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <Link
            to={`/dispensaries/${row.dispensary_id}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-100 hover:text-emerald-300 group"
          >
            {display}
            <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-zinc-300" />
          </Link>
          <div className="text-[11px] font-mono text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{row.dispensary_city || "—"}</span>
            {row.dispensary_license && (
              <>
                <span className="text-zinc-700">·</span>
                <span>{row.dispensary_license}</span>
              </>
            )}
            {row.assigned_rep_name && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-emerald-400">
                  Assigned: {row.assigned_rep_name}
                </span>
              </>
            )}
            {!row.assigned_rep_name && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-amber-400">Unassigned</span>
              </>
            )}
            {paused && (
              <>
                <span className="text-zinc-700">·</span>
                <span className="text-amber-400">PAUSED</span>
              </>
            )}
          </div>
        </div>
        {canWrite && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              data-testid={`priority-pause-${row.dispensary_id}`}
              size="sm"
              variant="ghost"
              onClick={onToggleStatus}
              className="h-7 text-xs text-zinc-400 hover:text-amber-300 hover:bg-amber-500/10"
              title={paused ? "Resume" : "Pause"}
            >
              {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
            </Button>
            <Button
              data-testid={`priority-remove-${row.dispensary_id}`}
              size="sm"
              variant="ghost"
              onClick={onRemove}
              className="h-7 text-xs text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10"
              title="Remove"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>
      {canWrite ? (
        <div className="flex items-start gap-2">
          <Input
            data-testid={`priority-notes-${row.dispensary_id}`}
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setDirty(e.target.value !== (row.partner_notes || ""));
            }}
            placeholder="Partner note (e.g. 'buyer prefers Thursdays')…"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-8 text-xs flex-1"
          />
          {dirty && (
            <Button
              data-testid={`priority-save-notes-${row.dispensary_id}`}
              size="sm"
              onClick={() => onEditNotes(notes)}
              className="h-8 bg-emerald-600 hover:bg-emerald-500 text-emerald-50 text-xs"
            >
              <Save className="w-3 h-3 mr-1" />
              Save
            </Button>
          )}
        </div>
      ) : (
        row.partner_notes && (
          <div className="text-xs text-zinc-400 italic bg-zinc-900/50 border-l-2 border-zinc-700 pl-2 py-1">
            {row.partner_notes}
          </div>
        )
      )}
    </li>
  );
}

// ---------------- Bulk CSV / paste import ----------------

function BulkImportDialog({ partner, existingIds, onClose, onDone }) {
  const [raw, setRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  // ambiguous resolutions: input → selected dispensary_id or "skip"
  const [ambiguousPicks, setAmbiguousPicks] = useState({});
  const [notes, setNotes] = useState("");

  const runPreview = async () => {
    if (!raw.trim()) {
      toast.info("Paste some lines first");
      return;
    }
    setPreviewing(true);
    try {
      const data = await previewBulkImportPriorities(partner.id, raw);
      setPreview(data);
      setAmbiguousPicks({});
    } catch (err) {
      toast.error("Preview failed", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    // IDs from matched (minus already-on-list), plus any ambiguous picks
    const matchedIds = preview.matched
      .filter((m) => !m.already)
      .map((m) => m.dispensary_id);
    const chosenAmbiguous = Object.values(ambiguousPicks).filter(
      (v) => v && v !== "skip"
    );
    const allIds = [...new Set([...matchedIds, ...chosenAmbiguous])];
    if (!allIds.length) {
      toast.info("Nothing new to commit");
      return;
    }
    setCommitting(true);
    try {
      const res = await confirmBulkImportPriorities(partner.id, allIds, notes);
      toast.success(
        `Imported ${res.inserted} priorit${res.inserted === 1 ? "y" : "ies"}`,
        {
          description:
            res.already_existed > 0
              ? `${res.already_existed} already existed · skipped`
              : undefined,
        }
      );
      onDone();
    } catch (err) {
      toast.error("Import failed", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="bulk-import-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Bulk import priorities · {partner.name}
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Paste the partner's list. One per line — license numbers,
            dispensary names, or nicknames all work. CSV rows are
            accepted (we read the first cell). We'll match against the
            DCC database and show you anything ambiguous before
            committing.
          </p>
        </DialogHeader>

        {!preview ? (
          <>
            <div className="flex flex-col gap-2 pt-2 flex-1 overflow-y-auto">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
                Paste list
              </Label>
              <Textarea
                data-testid="bulk-import-textarea"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={`C10-0000400-LIC
Mikeys House of Flower
14th street
...`}
                rows={10}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-xs resize-y"
              />
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 pt-2">
                Partner notes (optional, applied to every imported row)
              </Label>
              <Input
                data-testid="bulk-import-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Focus for Q2 flower push…"
                className="bg-zinc-900 border-zinc-800 text-zinc-100"
              />
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
              >
                Cancel
              </Button>
              <Button
                data-testid="bulk-import-preview-btn"
                onClick={runPreview}
                disabled={previewing || !raw.trim()}
                className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
              >
                {previewing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Preview matches</>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <PreviewPane
            preview={preview}
            existingIds={existingIds}
            ambiguousPicks={ambiguousPicks}
            setAmbiguousPicks={setAmbiguousPicks}
            committing={committing}
            onBack={() => setPreview(null)}
            onCommit={commit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewPane({
  preview,
  existingIds: _existingIds,
  ambiguousPicks,
  setAmbiguousPicks,
  committing,
  onBack,
  onCommit,
}) {
  const matchedNew = preview.matched.filter((m) => !m.already);
  const matchedExisting = preview.matched.filter((m) => m.already);
  const willInsert =
    matchedNew.length +
    Object.values(ambiguousPicks).filter((v) => v && v !== "skip").length;

  return (
    <>
      <div className="flex-1 overflow-y-auto pt-2 space-y-3 pr-1">
        <div className="flex items-center gap-2 flex-wrap text-xs font-mono">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
            <CheckCircle2 className="w-3 h-3" />
            {matchedNew.length} new
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-700 bg-zinc-800 text-zinc-400">
            {matchedExisting.length} already on list
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
            <AlertTriangle className="w-3 h-3" />
            {preview.ambiguous.length} ambiguous
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300">
            <XCircle className="w-3 h-3" />
            {preview.not_found.length} not found
          </span>
        </div>

        {matchedNew.length > 0 && (
          <section data-testid="preview-matched-new">
            <h4 className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 mb-1">
              New ({matchedNew.length})
            </h4>
            <ul className="space-y-1">
              {matchedNew.map((m, i) => (
                <li
                  key={`new-${i}`}
                  className="text-xs text-zinc-200 panel p-2 flex items-center gap-2 flex-wrap"
                >
                  <span className="text-emerald-400">
                    <CheckCircle2 className="w-3 h-3 inline" />
                  </span>
                  <span className="font-mono text-zinc-500">{m.input}</span>
                  <span className="text-zinc-500">→</span>
                  <span className="font-medium">{m.nickname || m.name}</span>
                  <span className="text-[11px] font-mono text-zinc-500">
                    {m.city} · {m.license || "no license"}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {matchedExisting.length > 0 && (
          <section data-testid="preview-matched-existing">
            <h4 className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">
              Already on list ({matchedExisting.length})
            </h4>
            <ul className="space-y-1 opacity-70">
              {matchedExisting.map((m, i) => (
                <li
                  key={`exist-${i}`}
                  className="text-xs text-zinc-400 panel p-2 flex items-center gap-2 flex-wrap"
                >
                  <span className="font-mono text-zinc-500">{m.input}</span>
                  <span className="text-zinc-700">→</span>
                  <span>{m.nickname || m.name}</span>
                  <span className="text-[11px] font-mono text-zinc-600">
                    (no-op)
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {preview.ambiguous.length > 0 && (
          <section data-testid="preview-ambiguous">
            <h4 className="text-[10px] font-mono uppercase tracking-wider text-amber-300 mb-1">
              Ambiguous — pick the right match ({preview.ambiguous.length})
            </h4>
            <ul className="space-y-2">
              {preview.ambiguous.map((a, i) => (
                <li key={`amb-${i}`} className="panel p-2">
                  <div className="text-xs text-zinc-300 mb-1 flex items-center gap-1 flex-wrap">
                    <AlertTriangle className="w-3 h-3 text-amber-400" />
                    <span className="font-mono text-zinc-500">{a.input}</span>
                  </div>
                  <Select
                    value={ambiguousPicks[a.input] || "skip"}
                    onValueChange={(v) =>
                      setAmbiguousPicks({ ...ambiguousPicks, [a.input]: v })
                    }
                  >
                    <SelectTrigger
                      data-testid={`ambiguous-select-${i}`}
                      className="bg-zinc-900 border-zinc-800 text-zinc-100 h-8 text-xs"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <SelectItem value="skip">
                        — Skip this line —
                      </SelectItem>
                      {a.candidates.map((c) => (
                        <SelectItem
                          key={c.dispensary_id}
                          value={c.dispensary_id}
                        >
                          {c.nickname || c.name} · {c.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </li>
              ))}
            </ul>
          </section>
        )}

        {preview.not_found.length > 0 && (
          <section data-testid="preview-not-found">
            <h4 className="text-[10px] font-mono uppercase tracking-wider text-rose-300 mb-1">
              Not found ({preview.not_found.length}) — these will be skipped
            </h4>
            <ul className="space-y-1">
              {preview.not_found.map((n, i) => (
                <li
                  key={`nf-${i}`}
                  className="text-xs text-zinc-500 panel p-2 flex items-center gap-2"
                >
                  <XCircle className="w-3 h-3 text-rose-400" />
                  <span className="font-mono">{n}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <DialogFooter className="gap-2 pt-2 border-t border-zinc-800">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
        >
          Back
        </Button>
        <Button
          data-testid="bulk-import-confirm-btn"
          onClick={onCommit}
          disabled={committing || willInsert === 0}
          className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-emerald-50"
        >
          {committing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Plus className="w-4 h-4 mr-1.5" />
              Import {willInsert} {willInsert === 1 ? "priority" : "priorities"}
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
